import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { AdvancedLifeAnalysisResult, LifeThemeId } from '../types/advancedLifeAnalysis';
import { calculateNativeLifeSeason, LifeSeason } from '../utils/v520/vedicDestinyMilestoneEngine';
import { LIFE_THEMES_REGISTRY } from '../utils/advancedLifeAnalysisTaxonomy';
import {
  Calendar,
  Clock,
  Compass,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Shield,
  Layers,
  ChevronRight,
  Activity,
  Award,
  Zap,
  Info
} from 'lucide-react';

export type ForecastHorizon = 'current_antar' | 'next_antar' | 'mahadasha_arc' | '1y' | '3y' | '10y';

interface LifePhaseForecastModuleProps {
  kundali: CompleteKundali;
  analysisResult?: AdvancedLifeAnalysisResult;
  selectedThemeId?: LifeThemeId;
  lang?: Language;
  className?: string;
  defaultHorizon?: ForecastHorizon;
  defaultEpoch?: ForecastHorizon;
}

interface HorizonForecastData {
  id: ForecastHorizon;
  labelEn: string;
  labelHi: string;
  subLabelEn: string;
  subLabelHi: string;
  calendarSpan: string;
  targetAge: number;
  projectedSeason: LifeSeason;
  isTransition: boolean;
  transitionAge?: number;
  transitionYear?: number;
  seasonProgressPercent: number;
  domainMomentumScore: number;
  domainStatusTagEn: string;
  domainStatusTagHi: string;
  dashaSummaryEn: string;
  dashaSummaryHi: string;
  gocharSummaryEn: string;
  gocharSummaryHi: string;
  turningPointWindowEn: string;
  turningPointWindowHi: string;
  domainNarrativeEn: string;
  domainNarrativeHi: string;
  dosEn: string[];
  dosHi: string[];
  dontsEn: string[];
  dontsHi: string[];
  trapdoorEn: string;
  trapdoorHi: string;
  upayaEn: string;
  upayaHi: string;
  philosophicalShiftEn: string;
  philosophicalShiftHi: string;
}

export const LifePhaseForecastModule: React.FC<LifePhaseForecastModuleProps> = ({
  kundali,
  analysisResult,
  selectedThemeId = 'career_job',
  lang = 'en',
  className = '',
  defaultHorizon = 'current_antar',
  defaultEpoch
}) => {
  const isHi = lang === 'hi';
  const [activeHorizon, setActiveHorizon] = useState<ForecastHorizon>(defaultEpoch || defaultHorizon || 'current_antar');
  const [showSeasonDrawer, setShowSeasonDrawer] = useState<boolean>(false);

  // 1. Current native age & active life season
  const { currentSeason, allSeasons, currentAge } = useMemo(() => {
    return calculateNativeLifeSeason(kundali);
  }, [kundali]);

  // 2. Active theme resolution
  const activeThemeId: LifeThemeId = analysisResult?.themeId || selectedThemeId || 'career_job';
  const themeConfig = analysisResult?.themeConfig || LIFE_THEMES_REGISTRY[activeThemeId] || LIFE_THEMES_REGISTRY['career_job'];
  const natalPromiseScore = analysisResult?.natalPromise?.score ?? 82;

  // 3. Dasha context
  const dashaList = Array.isArray(kundali?.dashas) ? kundali.dashas : [];
  const md = dashaList.find(d => d.isCurrent) || dashaList[0];
  const ad = md?.antardashas?.find(a => a.isCurrent) || md?.antardashas?.[0];
  const pd = ad?.antardashas?.find(p => p.isCurrent) || ad?.antardashas?.[0];

  const mdName = md?.planet || 'Active Lord';
  const adName = ad?.planet || 'Sub Lord';
  const pdName = pd?.planet || 'Micro Lord';

  const currentYear = new Date().getFullYear();
  const currentMonthIdx = new Date().getMonth();
  const monthNamesEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthNamesHi = ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'];

  // 4. Generate dynamic forecasts for 1y, 3y, and 10y horizons based on current life domain data
  const horizonForecasts = useMemo<Record<ForecastHorizon, HorizonForecastData>>(() => {
    // Helper to find season for target age
    const getSeasonForAge = (age: number): LifeSeason => {
      if (age <= 26) return allSeasons.find(s => s.id === 'season_vidya') || allSeasons[0];
      if (age <= 38) return allSeasons.find(s => s.id === 'season_sthapana') || allSeasons[1];
      if (age <= 52) return allSeasons.find(s => s.id === 'season_pratishtha') || allSeasons[2];
      if (age <= 65) return allSeasons.find(s => s.id === 'season_paripakvata') || allSeasons[3];
      return allSeasons.find(s => s.id === 'season_moksha') || allSeasons[4];
    };

    // Calculate progress within current season
    const calculateProgress = (age: number, season: LifeSeason) => {
      let min = 16, max = 26;
      if (season.id === 'season_vidya') { min = 16; max = 26; }
      else if (season.id === 'season_sthapana') { min = 26; max = 38; }
      else if (season.id === 'season_pratishtha') { min = 38; max = 52; }
      else if (season.id === 'season_paripakvata') { min = 52; max = 65; }
      else if (season.id === 'season_moksha') { min = 65; max = 85; }

      const clamped = Math.max(min, Math.min(max, age));
      return Math.round(((clamped - min) / (max - min)) * 100);
    };

    // --- Domain-Specific Intelligence Generators ---
    const getDomainOutlook = (theme: LifeThemeId, horizon: ForecastHorizon) => {
      switch (theme) {
        case 'house_property':
          if (horizon === '1y') {
            return {
              score: Math.min(96, natalPromiseScore + 4),
              statusEn: 'Registry & Acquisition Window Active',
              statusHi: 'रजिस्ट्री एवं भू-स्वामित्व प्राप्ति काल',
              narrativeEn: `Under active ${mdName}-${adName} Dasha and 4th house double-transit, initial legal title verification, mutation approvals, and token financial booking reach peak favorability. Ideal for securing clear freehold title deeds.`,
              narrativeHi: `सक्रिय ${mdName}-${adName} दशा एवं चतुर्थ भाव पर शुभ गोचर के प्रभाव से रजिस्ट्री, नामांतरण एवं अचल संपत्ति के प्रारंभिक अनुबंध हेतु यह समय सर्वोत्तम है। विवादरहित फ्रीहोल्ड संपत्ति का चयन करें।`,
              dosEn: ['Verify revenue mutation records with a legal counsel', 'Inspect municipal zoning approvals before token advances', 'Close non-essential high-interest bank debt'],
              dosHi: ['अनुभवी वकील से राजस्व अभिलेखों व नामांतरण की जांच कराएं', 'बयाना देने से पूर्व नगर निगम स्वीकृति अवश्य देखें', 'अनावश्यक महंगे कर्जों को समाप्त करें'],
              dontsEn: ['Do not rely on unverified verbal promises from promoters', 'Avoid agricultural land with disputed co-shares'],
              dontsHi: ['मौखिक वादों पर विश्वास न करें', 'विवादित संयुक्त खाते वाली कृषि भूमि से बचें'],
              trapdoorEn: 'Over-leveraging bank debt beyond 40% of liquid monthly cash inflow.',
              trapdoorHi: 'मासिक शुद्ध आय के ४०% से अधिक बैंक ऋण (ईएमआई) का बोझ लेना।',
              upayaEn: 'Offer red vermillion to Lord Hanuman on Tuesdays and recite Bhoomi Suktam.',
              upayaHi: 'मंगलवार को श्री हनुमान जी को सिंदूर अर्पित करें व भूमि सूक्त का पाठ करें।'
            };
          } else if (horizon === '3y') {
            return {
              score: Math.min(95, natalPromiseScore + 7),
              statusEn: 'Multi-Asset Fortification & Construction Ingress',
              statusHi: 'स्थाई निर्माण एवं बहु-संपत्ति संचय योग',
              narrativeEn: `As Saturn anchors the foundational Bhava and Jupiter activates the 11th from 4th, primary residential or commercial property construction completes. Tangible illiquid equity anchors family security.`,
              narrativeHi: `शनि के आधार भाव में स्थिरीकरण एवं गुरु के लाभ भाव पर दृष्टि से भवन निर्माण पूर्ण होगा तथा पारिवारिक अचल संपत्ति का सुदृढ़ आधार तैयार होगा।`,
              dosEn: ['Complete formal Griha Pravesh in an auspicious planetary muhurta', 'Formalize partition or joint ownership deeds unambiguously', 'Reinvest commercial rental cash flow into prime land'],
              dosHi: ['शुभ मुहूर्त में विधिवत गृह प्रवेश करें', 'संयुक्त स्वामित्व विलेख स्पष्ट रूप से पंजीकृत कराएं', 'किराए की आय को सुरक्षित भूखंडों में पुनः लगाएं'],
              dontsEn: ['Do not enter informal joint venture arrangements with distant relatives', 'Avoid delaying building tax and registry compliance'],
              dontsHi: ['दूर के संबंधियों के साथ अनौपचारिक साझेदारी न करें', 'संपत्ति कर व वैधानिक अनुपालन में देरी न करें'],
              trapdoorEn: 'Construction budget escalation draining liquid emergency buffers.',
              trapdoorHi: 'निर्माण लागत में अनपेक्षित वृद्धि के कारण आपातकालीन नकदी समाप्त होना।',
              upayaEn: 'Perform Vastu Shanti and install a consecrated Sri Yantra at home.',
              upayaHi: 'गृह में वास्तु शांति पूजन कराएं तथा श्री यन्त्र स्थापित करें।'
            };
          } else {
            return {
              score: Math.min(97, natalPromiseScore + 9),
              statusEn: 'Generational Batwara & Empire Estate Preservation',
              statusHi: 'पैतृक विरासत बँटवारा एवं संस्थागत संरक्षण',
              narrativeEn: `Across the 10-year epoch, complete resolution of ancestral estate (Batwara), partition agreements with siblings, and establishment of an irrevocable Family Asset Trust take place smoothly without court trials.`,
              narrativeHi: `१०-वर्षीय युगांतर में पैतृक संपत्ति बँटवारा, भाई-बहनों के मध्य सौहार्दपूर्ण समझौता तथा पारिवारिक ट्रस्ट का निर्माण पूर्ण होगा।`,
              dosEn: ['Draft an irrevocable family trust deed for successors', 'Distribute commercial revenue rights equitably to prevent progeny disputes', 'Settle pending municipal or boundary markers permanently'],
              dosHi: ['उत्तराधिकारियों हेतु पंजीकृत पारिवारिक ट्रस्ट विलेख तैयार कराएं', 'विवाद टालने हेतु राजस्व अधिकारों का न्यायसंगत वितरण करें', 'सीमा विवादों का स्थायी निपटारा करें'],
              dontsEn: ['Never leave testamentary wills unregistered or ambiguous', 'Avoid emotional favoritism among heirs'],
              dontsHi: ['वसीयत को अपंजीकृत या अस्पष्ट न छोड़ें', 'संतान के मध्य भावनात्मक पक्षपात से बचें'],
              trapdoorEn: 'Prolonging Batwara execution until dasha sandhi causing sudden tax litigation.',
              trapdoorHi: 'दशा संधि काल तक बँटवारे को टालना जिससे कानूनी विवाद उपजे।',
              upayaEn: 'Offer sesame oil deepam under Peepal tree on Saturdays and feed cows on Amavasya.',
              upayaHi: 'शनिवार को पीपल वृक्ष के नीचे तिल के तेल का दीपक जलाएं तथा अमावस्या को गो-सेवा करें।'
            };
          }

        case 'career_job':
        case 'business':
          if (horizon === '1y') {
            return {
              score: Math.min(95, natalPromiseScore + 5),
              statusEn: 'Executive Role Elevation & Revenue Ingress',
              statusHi: 'वरिष्ठ पदभार, मान-सम्मान एवं आय वृद्धि काल',
              narrativeEn: `Active ${mdName}-${adName} period with 10th/11th house stimulation triggers major executive advancement, strategic restructuring, and substantial compensation re-negotiation. High momentum for taking key ownership.`,
              narrativeHi: `सक्रिय ${mdName}-${adName} दशा एवं दशम-एकादश भाव के संमिलन से उच्च पदोन्नति, रणनीतिक दायित्व व वेतन पुनरीक्षण के स्पष्ट संकेत हैं। नई जिम्मेदारियां तुरंत स्वीकार करें।`,
              dosEn: ['Lead high-visibility enterprise transformations', 'Negotiate performance-linked equity and retainers', 'Cultivate direct sponsorship with C-suite / Board'],
              dosHi: ['उच्च प्रभाव वाली संगठनात्मक परियोजनाओं का नेतृत्व करें', 'वेतन के साथ लाभांश (ईसॉप्स) की मांग करें', 'संस्थान के शीर्ष नेतृत्व से सीधा संपर्क बनाएं'],
              dontsEn: ['Avoid premature public disclosure of strategic discussions', 'Do not engage in petty corporate rivalry'],
              dontsHi: ['योजनाओं को समय से पूर्व सार्वजनिक न करें', 'सहकर्मियों की व्यर्थ राजनीति में न उलझें'],
              trapdoorEn: 'Cognitive burnout and Pitta metabolic overload from excessive overtime.',
              trapdoorHi: 'अत्यधिक कार्यभार के कारण मानसिक तनाव व पित्त संबंधी स्वास्थ्य समस्या।',
              upayaEn: 'Offer Surya Arghya daily with red sandalwood and recite Aditya Hrudayam.',
              upayaHi: 'प्रतिदिन सूर्य देव को रोली-युक्त जल से अर्घ्य दें और आदित्य हृदय स्तोत्र पढ़ें।'
            };
          } else if (horizon === '3y') {
            return {
              score: Math.min(94, natalPromiseScore + 8),
              statusEn: 'Autonomous Authority & Independent Practice Launch',
              statusHi: 'स्वतंत्र उद्यम एकाधिकार व स्वायत्त अधिकार योग',
              narrativeEn: `Double transit of Jupiter and Saturn across Upachaya houses unlocks independent advisory mandate or entrepreneurial vertical. Shifts identity from corporate execution to autonomous industry authority.`,
              narrativeHi: `उपचय भावों पर शनि व गुरु का संयुक्त गोचर स्वतंत्र व्यापार, कंसल्टिंग या नए उद्यम की मजबूत नींव रखता है। आप कर्मचारी से उद्योग के मार्गदर्शक के रूप में उभरेंगे।`,
              dosEn: ['Incorporate autonomous business vertical or partnership entity', 'Protect intellectual property and proprietary trade methods', 'Recruit trustworthy second-in-command operational managers'],
              dosHi: ['स्वतंत्र व्यापार इकाई या फर्म पंजीकृत करें', 'व्यावसायिक गोपनीयता व बौद्धिक संपदा सुरक्षित रखें', 'विश्वस्त परिचालन प्रबंधकों की नियुक्ति करें'],
              dontsEn: ['Do not over-borrow commercial bank debt before verifying cash-flow proof', 'Avoid unhedged market expansions'],
              dontsHi: ['नकदी प्रवाह की पुष्टि से पूर्व बड़ा व्यावसायिक ऋण न लें', 'अनावश्यक जोखिम भरे विस्तार से बचें'],
              trapdoorEn: 'Regulatory scrutiny or partner disagreements during nodal Rahu sub-transit.',
              trapdoorHi: 'राहु उप-गोचर में कानूनी अनुपालन अथवा साझीदार से मतभेद।',
              upayaEn: 'Recite Sri Suktam and Kanakadhara Stotram on Fridays; keep Kuber Yantra in office.',
              upayaHi: 'शुक्रवार को श्री सूक्त व कनकधारा स्तोत्र का पाठ करें; कार्यस्थल पर कुबेर यन्त्र रखें।'
            };
          } else {
            return {
              score: Math.min(98, natalPromiseScore + 10),
              statusEn: 'Industry Pinnacle & Institutional Legacy Monopoly',
              statusHi: 'उद्योग शिखर, जन-प्रभाव एवं स्थायी प्रतिष्ठा',
              narrativeEn: `Over the decade, Raja Yogas fruiting in D-10 Dashamsha and D-5 Panchamsa manifest broad civic authority, industry board directorships, and institutional footprint with nationwide repute.`,
              narrativeHi: `दशमांश D-10 व पंचांश D-5 में राजयोग के फलित होने से राष्ट्रीय स्तर पर प्रतिष्ठा, बोर्ड सदस्यता व संस्थागत प्रभाव का चरमोत्कर्ष होगा।`,
              dosEn: ['Transition from daily micro-management to sovereign board governance', 'Mentor next-generation leadership and establish institutional code', 'Channel wealth into educational or philanthropic foundations'],
              dosHi: ['दैनिक प्रबंधन छोड़कर बोर्ड स्तरीय रणनीतिक मार्गदर्शन संभालें', 'नवीन नेतृत्व को प्रशिक्षित करें', 'पूंजी का उपयोग जनकल्याण व धर्मार्थ कार्यों में करें'],
              dontsEn: ['Do not cling to executive vetoes when succession is warranted', 'Avoid speculative hubris'],
              dontsHi: ['उचित समय पर नियंत्रण सौंपने में संकोच न करें', 'अति-आत्मविश्वास से बचें'],
              trapdoorEn: 'Loss of personal lifestyle anonymity and private family hours.',
              trapdoorHi: 'सार्वजनिक जीवन के कारण पारिवारिक एकांत व व्यक्तिगत समय की कमी।',
              upayaEn: 'Support underprivileged students with vidya-daan and conduct annual Rudrabhisheka.',
              upayaHi: 'निर्धन छात्रों को विद्या-दान में सहायता करें तथा प्रतिवर्ष रुद्राभिषेक कराएं।'
            };
          }

        case 'marriage_relationship':
          if (horizon === '1y') {
            return {
              score: Math.min(94, natalPromiseScore + 3),
              statusEn: 'Deep Harmonization & Commitment Gate',
              statusHi: 'दांपत्य सौहार्द, प्रगाढ़ता एवं शुभ वार्ता काल',
              narrativeEn: `7th Lord aspected by benefic transit Jupiter establishes auspicious window for formalizing alliances, removing long-standing domestic misunderstandings, and syncing mutual financial values.`,
              narrativeHi: `सप्तमेश पर गुरु की शुभ दृष्टि से विवाह वार्ता पक्की होने, मतभेदों के शांतिपूर्ण समाधान तथा परस्पर विश्वास सुदृढ़ होने का समय है।`,
              dosEn: ['Conduct transparent discussions on financial priorities', 'Honor marital rituals and spend quality couple time', 'Respect in-laws and parental boundaries'],
              dosHi: ['पारस्परिक प्राथमिकताओं पर पारदर्शी संवाद करें', 'दांपत्य संबंधों को समय दें', 'माता-पिता व ससुराल पक्ष का यथोचित सम्मान करें'],
              dontsEn: ['Avoid dredging up resolved historical grievances', 'Do not let third-party relatives dictate marital choices'],
              dontsHi: ['बीती बातों को बार-बार न दोहराएं', 'तीसरे पक्ष के अनावश्यक हस्तक्षेप से बचें'],
              trapdoorEn: 'Maraka 7th lord ego triggers during Mars transit over Lagna/7th axis.',
              trapdoorHi: 'मंगल के गोचर में क्रोध अथवा अहंकारवश अनावश्यक तकरार।',
              upayaEn: 'Light a pure cow ghee lamp before Goddess Durga on Fridays and recite Argala Stotram.',
              upayaHi: 'शुक्रवार को मां दुर्गा के समक्ष गाय के घी का दीपक जलाएं तथा अर्गला स्तोत्र पढ़ें।'
            };
          } else if (horizon === '3y') {
            return {
              score: Math.min(93, natalPromiseScore + 6),
              statusEn: 'Domestic Foundation & Progeny / Family Union',
              statusHi: 'पारिवारिक नींव, संतान योग एवं संयुक्त संपदा',
              narrativeEn: `Convergence across D-9 Navamsha and 5th/7th houses strengthens joint real estate investment, parenthood blessings, and long-term domestic contentment.`,
              narrativeHi: `नवमांश D-9 व पंचम-सप्तम भाव की अनुकूलता से संयुक्त संपत्ति निर्माण, संतान सुख एवं स्थायी पारिवारिक सुख की प्राप्ति होगी।`,
              dosEn: ['Acquire real estate in joint spousal names', 'Plan child conception or family milestone events in benefic windows', 'Travel together on sacred pilgrimages'],
              dosHi: ['जीवनसाथी के साथ संयुक्त नाम से संपत्ति क्रय करें', 'संतान प्राप्ति के संकल्प हेतु शुभ गोचर का लाभ लें', 'तीर्थाटन करें'],
              dontsEn: ['Do not permit work stress to spill into domestic bedroom sanctum', 'Avoid keeping secret financial accounts'],
              dontsHi: ['कार्यक्षेत्र का तनाव घर पर न लाएं', 'वित्तीय मामलों में पूर्ण पारदर्शिता रखें'],
              trapdoorEn: 'Parenting style differences causing subtle domestic divergence.',
              trapdoorHi: 'संतान के लालन-पालन पर परस्पर भिन्न दृष्टिकोण।',
              upayaEn: 'Perform joint Lakshmi-Narayana archana on Ekadashi days.',
              upayaHi: 'एकादशी के दिन संयुक्त रूप से लक्ष्मी-नारायण पूजन करें।'
            };
          } else {
            return {
              score: Math.min(96, natalPromiseScore + 8),
              statusEn: 'Golden Matrimonial Grace & Progeny Settlement',
              statusHi: 'दांपत्य सिद्धि, संतान का विवाह व पारिवारिक गौरव',
              narrativeEn: `Over the decade, marital partnership transitions into deep spiritual companionship, celebrated progeny marriages, and peaceful joint custody of family heritage.`,
              narrativeHi: `१०-वर्षीय युगांतर में दांपत्य संबंध आध्यात्मिक मित्रता में बदलेगा, संतान का विवाह व प्रतिष्ठा स्थापित होगी।`,
              dosEn: ['Guide progeny into honorable marital alliances', 'Engage in joint philanthropic and spiritual service', 'Celebrate major wedding anniversaries with Kula-Devata pooja'],
              dosHi: ['संतान के सुयोग्य विवाह का मार्गदर्शन करें', 'धार्मिक व जनकल्याणकारी कार्यों में साथ दें', 'कुल-देवता का विधिवत पूजन करें'],
              dontsEn: ['Do not enforce outdated matrimonial conditions on grown children', 'Avoid emotional isolation'],
              dontsHi: ['युवा संतान पर पुराने पूर्वाग्रह न थोपें', 'संवाद बनाए रखें'],
              trapdoorEn: 'Empty nest syndrome causing temporary void without shared spiritual pursuits.',
              trapdoorHi: 'संतान के दूर जाने पर अकेलापन, जिसे ईश्वर-भक्ति से दूर किया जा सकता है।',
              upayaEn: 'Offer yellow vastra and flowers at Shiva-Parvati temple on Mondays.',
              upayaHi: 'सोमवार को शिव-पार्वती मंदिर में पीले वस्त्र व पुष्प अर्पित करें।'
            };
          }

        case 'wealth_finance':
        default:
          if (horizon === '1y') {
            return {
              score: Math.min(93, natalPromiseScore + 4),
              statusEn: 'Capital Accumulation & Debt Consolidation',
              statusHi: 'धन संचय, ऋण मुक्ति व सुरक्षित निवेश काल',
              narrativeEn: `Jupiter transiting auspicious trine from natal Moon and activating the 2nd Bhava of Dhana ensures robust liquid inflow. Best utilized to eliminate high-interest liabilities and build 6-month liquid treasury.`,
              narrativeHi: `चंद्रमा से शुभ गोचर व द्वितीय भाव की सक्रियता से धन आगमन में सुगमता रहेगी। पुराने ऋण चुकाएं तथा सुरक्षित बचत निधि बनाएं।`,
              dosEn: ['Lock surplus liquid cash into sovereign gold bonds or fixed deposits', 'Re-balance equity portfolio to capture annual highs', 'Clear variable-rate personal loans'],
              dosHi: ['अतिरिक्त धन को सुरक्षित बचत व स्वर्ण में लगाएं', 'शेयर बाजार के लाभ को सुरक्षित फंड में ट्रांसफर करें', 'कर्ज समाप्त करें'],
              dontsEn: ['Do not gamble in unhedged F&O derivatives or volatile crypto', 'Avoid lending large unsecured sums to friends'],
              dontsHi: ['सट्टे या जोखिम भरे शेयर व्यापार से दूर रहें', 'मित्रों को बिना लिखा-पढ़ी के बड़ा उधार न दें'],
              trapdoorEn: 'Impulsive luxury expenditure under Rahu sub-transit illusion.',
              trapdoorHi: 'दिखावे व विलासिता में अचानक भारी धन अपव्यय।',
              upayaEn: 'Chant Kanakadhara Stotram on Fridays and feed birds daily at dawn.',
              upayaHi: 'शुक्रवार को कनकधारा स्तोत्र पढ़ें तथा नित्य पक्षियों को दाना डालें।'
            };
          } else if (horizon === '3y') {
            return {
              score: Math.min(95, natalPromiseScore + 7),
              statusEn: 'Commercial Equity Compounding & Asset Anchoring',
              statusHi: 'व्यावसायिक पूंजी वृद्धि एवं अचल संपत्ति विस्तार',
              narrativeEn: `With Dhana-Yoga combinations awakening across D-2 Hora and D-11 Ekadashamsha, wealth transitions from simple wage income to multi-stream capital returns and commercial real estate yields.`,
              narrativeHi: `होरा D-2 व एकादशांश D-11 में धनयोग फलित होने से आय के नए साधन बनेंगे और किराए या व्यापारिक लाभांश से स्थाई धन वृद्धि होगी।`,
              dosEn: ['Acquire high-yield commercial rental real estate', 'Establish systematic family investment trust', 'Diversify across real assets, gold, and blue-chip equities'],
              dosHi: ['किराए योग्य व्यावसायिक संपत्ति खरीदें', 'पारिवारिक निवेश व्यवस्था सुदृढ़ करें', 'सोना, जमीन व सुरक्षित इक्विटी में संतुलन रखें'],
              dontsEn: ['Do not concentrate more than 35% net worth into a single illiquid asset', 'Avoid tax non-compliance'],
              dontsHi: ['कुल पूंजी का ३५% से अधिक किसी एक ही जगह न फंसाएं', 'कर अनुपालन में लापरवाही न करें'],
              trapdoorEn: 'Over-estimating future revenue growth before securing counter-party guarantees.',
              trapdoorHi: 'भविष्य के मुनाफे का अति-आकलन कर वर्तमान खर्च बढ़ाना।',
              upayaEn: 'Keep a silver coin or Sri Yantra in home vault and respect daughters/sisters as Lakshmi.',
              upayaHi: 'घर की तिजोरी में चांदी का सिक्का अथवा श्री यन्त्र रखें तथा कन्याओं का सम्मान करें।'
            };
          } else {
            return {
              score: Math.min(97, natalPromiseScore + 10),
              statusEn: 'Dynastic Wealth Fortress & Irrevocable Trusts',
              statusHi: 'स्थाई संपदा दुर्ग, पारिवारिक ट्रस्ट व वंशानुगत समृद्धि',
              narrativeEn: `Over 10 years, full realization of Lakshmi Yogas establishes enduring financial independence, insulating the lineage against market shocks and funding multi-generational prosperity.`,
              narrativeHi: `१० वर्षों में महालक्ष्मी योग पूर्णतः जागृत होगा, जिससे कई पीढ़ियों हेतु सुरक्षित वित्तीय साम्राज्य व ट्रस्ट स्थापित होगा।`,
              dosEn: ['Set up an irrevocable family private trust with clear succession guidelines', 'Allocate 10% annual gains into sacred dharmic philanthropy (Annam Daan)', 'Appoint independent legal audit counsels'],
              dosHi: ['स्पष्ट उत्तराधिकार नियमों वाला पारिवारिक ट्रस्ट बनाएं', 'वार्षिक लाभ का १०% धर्मार्थ व अन्न-दान में लगाएं', 'स्वतंत्र वित्तीय सलाहकार नियुक्त करें'],
              dontsEn: ['Never leave cross-border or ancestral holdings unregistered', 'Avoid financial secrecy with spouse'],
              dontsHi: ['संपत्तियों को बिना वसीयत या नामांकन के न छोड़ें', 'पारिवारिक पारदर्शिता रखें'],
              trapdoorEn: 'Family disputes over estate division if clear trust deeds are delayed.',
              trapdoorHi: 'वसीयत में विलंब के कारण संतान के बीच संपत्ति का विवाद।',
              upayaEn: 'Perform Vishnu-Sahasranama with tulsi leaves on Thursdays and donate blankets to needy in winter.',
              upayaHi: 'गुरुवार को तुलसी दल से विष्णु सहस्रनाम पाठ करें व शीतकाल में निर्धनों को कंबल दान करें।'
            };
          }
      }
    };

    // Calculate dates & age projections
    // 1 Year
    const age1y = currentAge + 1;
    const season1y = getSeasonForAge(age1y);
    const domain1y = getDomainOutlook(activeThemeId, '1y');

    // 3 Years
    const age3y = currentAge + 3;
    const season3y = getSeasonForAge(age3y);
    const domain3y = getDomainOutlook(activeThemeId, '3y');

    // 10 Years
    const age10y = currentAge + 10;
    const season10y = getSeasonForAge(age10y);
    const domain10y = getDomainOutlook(activeThemeId, '10y');

    const baseForecasts: Record<'1y' | '3y' | '10y', HorizonForecastData> = {
      '1y': {
        id: '1y',
        labelEn: '1-Year Outlook',
        labelHi: '१-वर्षीय फलादेश',
        subLabelEn: 'Immediate Ingress & Varshaphala Gate',
        subLabelHi: 'तात्कालिक गोचर व वर्षफल द्वार',
        calendarSpan: `${monthNamesEn[currentMonthIdx]} ${currentYear} – ${monthNamesEn[currentMonthIdx]} ${currentYear + 1}`,
        targetAge: age1y,
        projectedSeason: season1y,
        isTransition: season1y.id !== currentSeason.id,
        transitionAge: season1y.id !== currentSeason.id ? age1y : undefined,
        transitionYear: season1y.id !== currentSeason.id ? currentYear + 1 : undefined,
        seasonProgressPercent: calculateProgress(age1y, season1y),
        domainMomentumScore: domain1y.score,
        domainStatusTagEn: domain1y.statusEn,
        domainStatusTagHi: domain1y.statusHi,
        dashaSummaryEn: `${mdName} Mahadasha • ${adName} Antardasha (${pdName} Pratyantar)`,
        dashaSummaryHi: `${mdName} महादशा • ${adName} अंतर्दशा (${pdName} प्रत्यंतर सक्रिय)`,
        gocharSummaryEn: `Jupiter in favorable trine from Moon; Saturn stabilizing Upachaya houses`,
        gocharSummaryHi: `गुरु का चंद्रमा से त्रिकोण गोचर; शनि द्वारा उपचय भावों में सुदृढ़ीकरण`,
        turningPointWindowEn: `Oct 2026 – Mar 2027 (Peak Solar & Pratyantar Confluence)`,
        turningPointWindowHi: `अक्टूबर २०२६ – मार्च २०२७ (सूर्य गोचर व प्रत्यंतर संमिलन)`,
        domainNarrativeEn: domain1y.narrativeEn,
        domainNarrativeHi: domain1y.narrativeHi,
        dosEn: domain1y.dosEn,
        dosHi: domain1y.dosHi,
        dontsEn: domain1y.dontsEn,
        dontsHi: domain1y.dontsHi,
        trapdoorEn: domain1y.trapdoorEn,
        trapdoorHi: domain1y.trapdoorHi,
        upayaEn: domain1y.upayaEn,
        upayaHi: domain1y.upayaHi,
        philosophicalShiftEn: `Consolidating current season readiness. Focus on tactical discipline, personal grounding, and seizing time-sensitive windows.`,
        philosophicalShiftHi: `वर्तमान जीवन चरण का सुदृढ़ीकरण। अनुशासन, आत्मविश्वास व समय पर निर्णय लेने पर बल दें।`
      },
      '3y': {
        id: '3y',
        labelEn: '3-Year Outlook',
        labelHi: '३-वर्षीय फलादेश',
        subLabelEn: 'Antardasha Pivot & Multi-Year Transits',
        subLabelHi: 'अंतरदशा परिवर्तन व द्वि-गोचर फल',
        calendarSpan: `${currentYear + 1} – ${currentYear + 4}`,
        targetAge: age3y,
        projectedSeason: season3y,
        isTransition: season3y.id !== currentSeason.id,
        transitionAge: season3y.id !== currentSeason.id ? (currentSeason.id === 'season_pratishtha' ? 52 : currentSeason.id === 'season_sthapana' ? 38 : 26) : undefined,
        transitionYear: season3y.id !== currentSeason.id ? currentYear + 2 : undefined,
        seasonProgressPercent: calculateProgress(age3y, season3y),
        domainMomentumScore: domain3y.score,
        domainStatusTagEn: domain3y.statusEn,
        domainStatusTagHi: domain3y.statusHi,
        dashaSummaryEn: `${mdName} transitioning into key Benefic Sub-period in Kendra/Trikona`,
        dashaSummaryHi: `${mdName} दशा में केंद्र-त्रिकोण के शुभ उप-ग्रह का अंतर्दशा प्रवेश`,
        gocharSummaryEn: `Jupiter traversing 3 zodiac signs + Saturn completing 2.5-yr foundational shift`,
        gocharSummaryHi: `गुरु का ३ राशियों में भ्रमण + शनि का ढाई वर्षीय राशि परिवर्तन`,
        turningPointWindowEn: `Mid 2027 – Late 2028 (Double-Transit Confluence Gate)`,
        turningPointWindowHi: `मध्य २०२७ – उत्तर २०२८ (गुरु-शनि संयुक्त गोचर सिद्धि काल)`,
        domainNarrativeEn: domain3y.narrativeEn,
        domainNarrativeHi: domain3y.narrativeHi,
        dosEn: domain3y.dosEn,
        dosHi: domain3y.dosHi,
        dontsEn: domain3y.dontsEn,
        dontsHi: domain3y.dontsHi,
        trapdoorEn: domain3y.trapdoorEn,
        trapdoorHi: domain3y.trapdoorHi,
        upayaEn: domain3y.upayaEn,
        upayaHi: domain3y.upayaHi,
        philosophicalShiftEn: `Structural maturation of identity. Moving from active struggle to consolidated authority and long-term asset permanence.`,
        philosophicalShiftHi: `पहचान व सामाजिक प्रतिष्ठा का स्थिरीकरण। निरंतर संघर्ष से निकलकर स्थायी सत्ता व संपदा की स्थापना।`
      },
      '10y': {
        id: '10y',
        labelEn: '10-Year Outlook',
        labelHi: '१०-वर्षीय फलादेश',
        subLabelEn: 'Mahadasha Epoch & Season Graduation',
        subLabelHi: 'महादशा युगांतर व जीवन-ऋतु परिवर्तन',
        calendarSpan: `${currentYear + 1} – ${currentYear + 11}`,
        targetAge: age10y,
        projectedSeason: season10y,
        isTransition: season10y.id !== currentSeason.id,
        transitionAge: season10y.id !== currentSeason.id ? (currentSeason.id === 'season_pratishtha' ? 52 : currentSeason.id === 'season_sthapana' ? 38 : 65) : undefined,
        transitionYear: season10y.id !== currentSeason.id ? currentYear + (currentSeason.id === 'season_pratishtha' ? Math.max(1, 52 - currentAge) : 5) : undefined,
        seasonProgressPercent: calculateProgress(age10y, season10y),
        domainMomentumScore: domain10y.score,
        domainStatusTagEn: domain10y.statusEn,
        domainStatusTagHi: domain10y.statusHi,
        dashaSummaryEn: `Major Mahadasha Sovereignty Handover (Chhidra Dasha $\\rightarrow$ New Sovereign)`,
        dashaSummaryHi: `महादशा का ऐतिहासिक परिवर्तन (छिद्र दशा समापन व नवीन दशा स्वामी का उदय)`,
        gocharSummaryEn: `Complete Nodal Reverse Axis (18-yr Rahu-Ketu Cycle) + Saturn full quadrant`,
        gocharSummaryHi: `राहु-केतु का पूर्ण नोडल चक्र + शनि का सम्पूर्ण केंद्र चक्र भ्रमण`,
        turningPointWindowEn: `2030 – 2033 (Epochal Mahadasha & Karmic Maturity Apex)`,
        turningPointWindowHi: `२०३० – २०३३ (महादशा संक्रमण एवं कर्म परिपक्वता का शिखर)`,
        domainNarrativeEn: domain10y.narrativeEn,
        domainNarrativeHi: domain10y.narrativeHi,
        dosEn: domain10y.dosEn,
        dosHi: domain10y.dosHi,
        dontsEn: domain10y.dontsEn,
        dontsHi: domain10y.dontsHi,
        trapdoorEn: domain10y.trapdoorEn,
        trapdoorHi: domain10y.trapdoorHi,
        upayaEn: domain10y.upayaEn,
        upayaHi: domain10y.upayaHi,
        philosophicalShiftEn: `Epochal graduation from personal ambition to institutional stewardship, lineage protection, and spiritual detachment.`,
        philosophicalShiftHi: `व्यक्तिगत महत्वाकांक्षा से उठकर संस्थागत दायित्व, कुल-परंपरा संरक्षण एवं आत्म-चिंतन की ओर महा-प्रस्थान।`
      }
    };

    const periodData: Record<ForecastHorizon, HorizonForecastData> = {
      ...baseForecasts,
      'current_antar': {
        ...baseForecasts['1y'],
        id: 'current_antar',
        labelEn: 'Active Antardasha (Bhukti)',
        labelHi: 'सक्रिय अंतर्दशा (भुक्ति)',
        subLabelEn: `${mdName}–${adName} Active Window`,
        subLabelHi: `${mdName}–${adName} सक्रिय कालखंड`
      },
      'next_antar': {
        ...baseForecasts['3y'],
        id: 'next_antar',
        labelEn: 'Next Antardasha Transition',
        labelHi: 'आगामी अंतर्दशा संक्रान्ति',
        subLabelEn: 'Sub-period Ingress Gate',
        subLabelHi: 'आगामी उप-दशा प्रवेश'
      },
      'mahadasha_arc': {
        ...baseForecasts['10y'],
        id: 'mahadasha_arc',
        labelEn: 'Full Mahadasha Lifecycle',
        labelHi: 'संपूर्ण महादशा महाचक्र',
        subLabelEn: `${mdName} Major Arc`,
        subLabelHi: `${mdName} संपूर्ण महादशा`
      }
    };

    return periodData;
  }, [allSeasons, currentAge, currentSeason, mdName, adName, pdName, currentYear, currentMonthIdx, activeThemeId, natalPromiseScore]);

  const activeForecast = horizonForecasts[activeHorizon] || horizonForecasts['current_antar'] || horizonForecasts['1y'];

  // Visual position of age along life trajectory
  const getSeasonProgressWidth = (age: number) => {
    // 16 to 75 scale
    const clamped = Math.max(16, Math.min(75, age));
    return ((clamped - 16) / (75 - 16)) * 100;
  };

  return (
    <div
      id="life-phase-forecast-module"
      className={`bg-astro-card border border-astro-border rounded-2xl p-5 sm:p-6 shadow-xs space-y-6 ${className}`}
    >
      {/* 1. Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-astro-border/80 pb-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-astro-secondary-surface text-astro-primary border border-astro-border">
              <Compass className="w-4 h-4 text-astro-accent" />
            </span>
            <span className="text-xs font-bold uppercase tracking-wider text-astro-primary font-mono">
              {isHi ? 'जीवन कालखंड फलादेश एवं दशा संधि' : 'Life Phase Forecast & Dasha Transition'}
            </span>
            <span className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full bg-astro-secondary-surface text-amber-900 border border-astro-border">
              Age {currentAge}
            </span>
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-astro-text font-serif">
            {isHi
              ? `${themeConfig.nameHi} — दशा-गोचर कालिक फलादेश`
              : `${themeConfig.nameEn} — Astrological Period Outlook`}
          </h3>
          <p className="text-xs text-astro-secondary-text">
            {isHi
              ? 'सक्रिय जीवन ऋतु के संक्रमण, दशा चक्र एवं गोचर संमिलन के आधार पर चयनित जीवन क्षेत्र का विश्लेषण।'
              : 'Deterministic trajectory analyzing domain evolution across active Antardasha, upcoming transition, and full Mahadasha lifecycle.'}
          </p>
        </div>

        {/* Active Domain Pill & Promise */}
        <div className="flex items-center gap-2 self-start md:self-auto bg-astro-secondary-surface/70 px-3 py-2 rounded-xl border border-astro-border shrink-0">
          <div className="text-right">
            <div className="text-[10px] uppercase font-bold tracking-wider text-astro-secondary-text">
              {isHi ? 'चयनित शास्त्रीय क्षेत्र' : 'Active Life Domain'}
            </div>
            <div className="text-xs font-bold text-astro-primary font-serif">
              {isHi ? themeConfig.nameHi : themeConfig.nameEn}
            </div>
          </div>
          <div className="h-6 w-px bg-astro-border" />
          <div className="text-center">
            <div className="text-[10px] uppercase font-bold tracking-wider text-astro-secondary-text">
              {isHi ? 'जन्म प्रॉमिस' : 'Promise'}
            </div>
            <div className="text-xs font-mono font-black text-emerald-700">
              {natalPromiseScore}/100
            </div>
          </div>
        </div>
      </div>

      {/* 2. Visual Life Season Transition Stepper & Trajectory Gauge */}
      <div className="bg-astro-secondary-surface/50 border border-astro-border rounded-xl p-4 sm:p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-astro-accent shrink-0" />
            <span className="text-xs font-bold text-astro-text uppercase tracking-wider">
              {isHi ? 'जीवन ऋतु संक्रमण मानचित्र (Life Seasons Arc)' : 'Active Life Season Transition Arc'}
            </span>
          </div>

          <button
            onClick={() => setShowSeasonDrawer(!showSeasonDrawer)}
            className="text-[11px] font-bold text-astro-primary hover:text-astro-accent flex items-center gap-1 cursor-pointer"
          >
            <span>{showSeasonDrawer ? (isHi ? 'ऋतु विवरण छुपाएं' : 'Hide All Seasons') : (isHi ? '५ जीवन ऋतुएं देखें' : 'View All 5 Life Seasons')}</span>
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showSeasonDrawer ? 'rotate-90' : ''}`} />
          </button>
        </div>

        {/* Stepper Chain: The 5 Vedic Life Seasons */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-1">
          {allSeasons.map((season, idx) => {
            const isCurrentActive = season.id === currentSeason.id;
            const isProjectedTarget = season.id === activeForecast.projectedSeason.id;

            return (
              <div
                key={season.id}
                className={`p-2.5 rounded-lg border transition-all text-xs space-y-1 relative ${
                  isProjectedTarget && !isCurrentActive
                    ? 'bg-amber-50/80 border-amber-300 ring-2 ring-amber-400/30'
                    : isCurrentActive
                    ? 'bg-white border-astro-primary shadow-xs ring-2 ring-astro-primary/20'
                    : 'bg-white/50 border-astro-border/70 opacity-70 hover:opacity-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono font-bold text-astro-secondary-text">
                    {idx + 1}. {season.ageRange}
                  </span>
                  {isCurrentActive && (
                    <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">
                      NOW
                    </span>
                  )}
                  {isProjectedTarget && !isCurrentActive && (
                    <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-amber-100 text-amber-900 border border-amber-300 animate-pulse">
                      HORIZON
                    </span>
                  )}
                </div>

                <div className="font-bold text-astro-text font-serif text-[12px] truncate" title={isHi ? season.nameHi : season.nameEn}>
                  {isHi ? season.nameHi.split(' ')[0] : season.nameEn}
                </div>

                <div className="text-[10px] text-astro-secondary-text truncate font-mono">
                  {season.sanskritTerm}
                </div>
              </div>
            );
          })}
        </div>

        {/* Life Trajectory Progress Bar & Transition Pin */}
        <div className="pt-2 space-y-2">
          <div className="relative w-full h-3 bg-astro-border/60 rounded-full overflow-hidden">
            {/* Base fill up to current age */}
            <div
              className="absolute top-0 left-0 h-full bg-astro-primary rounded-full transition-all duration-500"
              style={{ width: `${getSeasonProgressWidth(currentAge)}%` }}
            />
            {/* Horizon projection forward extension */}
            <div
              className="absolute top-0 h-full bg-amber-500/70 transition-all duration-500"
              style={{
                left: `${getSeasonProgressWidth(currentAge)}%`,
                width: `${Math.max(0, getSeasonProgressWidth(activeForecast.targetAge) - getSeasonProgressWidth(currentAge))}%`
              }}
            />
          </div>

          <div className="flex items-center justify-between text-[11px] text-astro-secondary-text font-mono">
            <span>Age 16 (Youth Initiation)</span>
            <span className="font-bold text-astro-primary flex items-center gap-1">
              <span>Current Age: {currentAge}</span>
              <ArrowRight className="w-3 h-3 text-amber-600" />
              <span className="text-amber-800">Horizon: Age {activeForecast.targetAge}</span>
            </span>
            <span>Age 75+ (Moksha & Grace)</span>
          </div>
        </div>

        {/* Transition Alert / Status Banner */}
        <div className={`p-3 rounded-lg border text-xs flex items-start gap-2.5 ${
          activeForecast.isTransition
            ? 'bg-amber-50 border-amber-200 text-amber-950'
            : 'bg-white border-astro-border text-astro-text'
        }`}>
          {activeForecast.isTransition ? (
            <Zap className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          ) : (
            <Activity className="w-4 h-4 text-astro-primary shrink-0 mt-0.5" />
          )}

          <div className="space-y-0.5">
            <div className="font-bold flex items-center gap-1.5 flex-wrap">
              <span>
                {activeForecast.isTransition
                  ? (isHi ? '⚠️ ऋतु संक्रमण वेला (Life Season Transition Point Detected)' : '⚠️ Life Season Transition Threshold')
                  : (isHi ? 'स्थिर ऋतु विस्तार (Deepening Current Life Season)' : 'Consolidating Active Life Season')}
              </span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/80 border border-astro-border">
                {activeForecast.calendarSpan}
              </span>
            </div>

            <p className="text-[11px] text-astro-secondary-text leading-relaxed">
              {activeForecast.isTransition ? (
                isHi ? (
                  <>
                    आप <strong>{currentSeason.nameHi}</strong> ({currentSeason.ageRange}) से आगे बढ़कर लगभग आयु <strong>{activeForecast.transitionAge} वर्ष ({activeForecast.transitionYear})</strong> में <strong>{activeForecast.projectedSeason.nameHi}</strong> ({activeForecast.projectedSeason.ageRange}) में प्रवेश करेंगे। इस कालखंड में प्राथमिकताएं बदलेंगी: {activeForecast.philosophicalShiftHi}
                  </>
                ) : (
                  <>
                    You will graduate from <strong>{currentSeason.nameEn}</strong> ({currentSeason.ageRange}) into <strong>{activeForecast.projectedSeason.nameEn}</strong> ({activeForecast.projectedSeason.ageRange}) around <strong>Age {activeForecast.transitionAge} ({activeForecast.transitionYear})</strong>. Core developmental shift: {activeForecast.philosophicalShiftEn}
                  </>
                )
              ) : (
                isHi ? (
                  <>
                    आप वर्तमान में <strong>{currentSeason.nameHi}</strong> में सक्रिय हैं (ऋतु पूर्णता: <strong>{activeForecast.seasonProgressPercent}%</strong>)। इस कालखंड में इसी ऋतु की ऊर्जा को पूर्णता पर ले जाएं: {activeForecast.philosophicalShiftHi}
                  </>
                ) : (
                  <>
                    You remain in the full momentum of <strong>{currentSeason.nameEn}</strong> (Season Completion: <strong>{activeForecast.seasonProgressPercent}%</strong>). Focus on leveraging this season’s core assets: {activeForecast.philosophicalShiftEn}
                  </>
                )
              )}
            </p>
          </div>
        </div>

        {/* Collapsible Drawer for All 5 Life Seasons Details */}
        {showSeasonDrawer && (
          <div className="pt-3 border-t border-astro-border/70 space-y-3 animate-in fade-in duration-200">
            <div className="text-[11px] font-bold text-astro-secondary-text uppercase tracking-wider">
              {isHi ? '५ वैदिक जीवन ऋतुओं का शास्त्रीय स्वरूप:' : 'The 5 Parashari Life Seasons Classical Framework:'}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
              {allSeasons.map((s) => (
                <div key={s.id} className="bg-white p-3 rounded-lg border border-astro-border text-xs space-y-1">
                  <div className="flex justify-between items-center font-bold">
                    <span className="text-astro-primary">{isHi ? s.nameHi : s.nameEn}</span>
                    <span className="text-[10px] font-mono text-astro-muted">Age {s.ageRange}</span>
                  </div>
                  <div className="text-[10px] text-amber-900 font-mono font-semibold">
                    {s.sanskritTerm} • Bhavas: {s.primaryBhavas.join(', ')}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text line-clamp-2">
                    {isHi ? s.descriptionHi : s.descriptionEn}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 3. Astrological Period Selector Tabs */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 p-1 bg-astro-secondary-surface rounded-xl border border-astro-border shadow-2xs">
          {(['current_antar', 'next_antar', 'mahadasha_arc'] as ForecastHorizon[]).map((hz) => {
            const isSelected = activeHorizon === hz || (hz === 'current_antar' && activeHorizon === '1y') || (hz === 'next_antar' && activeHorizon === '3y') || (hz === 'mahadasha_arc' && activeHorizon === '10y');
            const hData = horizonForecasts[hz] || horizonForecasts['current_antar'];

            return (
              <button
                key={hz}
                onClick={() => setActiveHorizon(hz)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  isSelected
                    ? 'bg-astro-primary text-white shadow-xs'
                    : 'text-astro-secondary-text hover:text-astro-text hover:bg-white/60'
                }`}
              >
                <span>{isHi ? hData.labelHi : hData.labelEn}</span>
                <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded ${
                  isSelected ? 'bg-white/20 text-white' : 'bg-astro-border/50 text-astro-secondary-text'
                }`}>
                  {hz === 'current_antar' ? 'AD' : hz === 'next_antar' ? 'Next AD' : 'MD Arc'}
                </span>
              </button>
            );
          })}
        </div>

        <div className="text-xs font-mono font-semibold text-astro-secondary-text flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 text-astro-accent" />
          <span>{activeForecast.calendarSpan}</span>
        </div>
      </div>

      {/* 4. Active Horizon Domain Detailed Forecast Card */}
      <div className="space-y-4">
        {/* Top Status & Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Metric 1: Domain Readiness Score */}
          <div className="bg-white p-3.5 rounded-xl border border-astro-border shadow-2xs space-y-1">
            <span className="text-[10px] uppercase font-bold tracking-wider text-astro-secondary-text">
              {isHi ? 'क्षेत्र अनुकूलता स्कोर' : 'Domain Momentum Score'}
            </span>
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-black font-mono text-emerald-700">
                {activeForecast.domainMomentumScore}/100
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                {isHi ? 'प्रबल संमिलन' : 'High Synergy'}
              </span>
            </div>
            <div className="text-[11px] font-medium text-astro-primary truncate font-serif">
              {isHi ? activeForecast.domainStatusTagHi : activeForecast.domainStatusTagEn}
            </div>
          </div>

          {/* Metric 2: Dasha & Gochar Confluence */}
          <div className="bg-white p-3.5 rounded-xl border border-astro-border shadow-2xs space-y-1">
            <span className="text-[10px] uppercase font-bold tracking-wider text-astro-secondary-text">
              {isHi ? 'सक्रिय दशा एवं गोचर संरेखन' : 'Operating Dasha & Transit'}
            </span>
            <div className="text-xs font-mono font-bold text-astro-primary truncate">
              {isHi ? activeForecast.dashaSummaryHi : activeForecast.dashaSummaryEn}
            </div>
            <p className="text-[11px] text-astro-secondary-text line-clamp-2">
              {isHi ? activeForecast.gocharSummaryHi : activeForecast.gocharSummaryEn}
            </p>
          </div>

          {/* Metric 3: Critical Turning Point Window */}
          <div className="bg-white p-3.5 rounded-xl border border-astro-border shadow-2xs space-y-1">
            <span className="text-[10px] uppercase font-bold tracking-wider text-astro-secondary-text">
              {isHi ? 'निर्णायक सिद्धि कालखंड' : 'Key Turning Point Window'}
            </span>
            <div className="text-xs font-mono font-bold text-amber-900 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 text-astro-accent shrink-0" />
              <span>{isHi ? activeForecast.turningPointWindowHi : activeForecast.turningPointWindowEn}</span>
            </div>
            <p className="text-[11px] text-astro-secondary-text">
              {isHi ? 'इस अवधि में लिए गए निर्णय स्थायी प्रभाव छोड़ेंगे।' : 'Decisions finalized within this gate set enduring trajectories.'}
            </p>
          </div>
        </div>

        {/* Narrative & Strategic Directive Box */}
        <div className="bg-white p-4 sm:p-5 rounded-xl border border-astro-border shadow-2xs space-y-4">
          <div className="space-y-1.5">
            <h4 className="text-sm font-bold text-astro-text font-serif flex items-center gap-2">
              <Award className="w-4 h-4 text-astro-accent" />
              <span>
                {isHi
                  ? `${themeConfig.nameHi} — ${activeForecast.labelHi} विस्तृत विवेचन`
                  : `${themeConfig.nameEn} — ${activeForecast.labelEn} Strategic Synthesis`}
              </span>
            </h4>
            <p className="text-xs text-astro-secondary-text leading-relaxed">
              {isHi ? activeForecast.domainNarrativeHi : activeForecast.domainNarrativeEn}
            </p>
          </div>

          {/* Dos vs Don'ts Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-astro-border/70 text-xs">
            <div className="bg-emerald-50/50 p-3 rounded-lg border border-emerald-200/80 space-y-1.5">
              <span className="font-bold text-emerald-900 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                {isHi ? 'क्या करें (Strategic Action Mandates):' : 'Strategic Action Directives (Do’s):'}
              </span>
              <ul className="space-y-1 text-[11px] text-emerald-950">
                {(isHi ? activeForecast.dosHi : activeForecast.dosEn).map((d, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-emerald-600 font-bold shrink-0">•</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-rose-50/50 p-3 rounded-lg border border-rose-200/80 space-y-1.5">
              <span className="font-bold text-rose-900 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-rose-700" />
                {isHi ? 'क्या न करें (Guardrails & Avoidances):' : 'Crucial Guardrails (Don’ts):'}
              </span>
              <ul className="space-y-1 text-[11px] text-rose-950">
                {(isHi ? activeForecast.dontsHi : activeForecast.dontsEn).map((d, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-rose-600 font-bold shrink-0">•</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Trapdoor & Remedial Harmony Footer */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 text-xs">
            <div className="bg-astro-secondary-surface p-2.5 rounded-lg border border-astro-border flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <strong className="text-amber-900 block font-bold">
                  {isHi ? 'संभावित कर्म जाल (Karmic Trapdoor):' : 'Karmic Trapdoor to Avoid:'}
                </strong>
                <span className="text-[11px] text-astro-secondary-text">
                  {isHi ? activeForecast.trapdoorHi : activeForecast.trapdoorEn}
                </span>
              </div>
            </div>

            <div className="bg-astro-secondary-surface p-2.5 rounded-lg border border-astro-border flex items-start gap-2">
              <Shield className="w-4 h-4 text-astro-primary shrink-0 mt-0.5" />
              <div>
                <strong className="text-astro-primary block font-bold">
                  {isHi ? 'शास्त्रीय उपाय एवं सात्विक साधना:' : 'Classical Upaya & Harmony Practice:'}
                </strong>
                <span className="text-[11px] text-astro-secondary-text">
                  {isHi ? activeForecast.upayaHi : activeForecast.upayaEn}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
